import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Join Company Component - FIXED: Uses correct endpoint for authenticated users
import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    Container,
    Paper,
    useTheme,
    CircularProgress,
    LinearProgress
} from '@mui/material';
import {
    Business as BusinessIcon,
    PersonAdd as PersonAddIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useAuth } from '../../hooks/useAuth';
import ArsenalLogo from '../common/ArsenalLogo';
import { useNotification } from '../common/GlobalNotifications';
import { api } from '../../services/api';

const validationSchema = Yup.object({
    invitationCode: Yup.string()
        .required(i18n.t('hardcoded.components_auth_joincompany.el_codigo_de_invitacion_es'))
        .matches(
            /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
            i18n.t('hardcoded.components_auth_joincompany.formato_de_codigo_de_invitacion')
        ),
});

const JoinCompany = () => {
  const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, isAuthenticated } = useAuth();
    const { showSuccess, showError } = useNotification();

    const [joinError, setJoinError] = useState('');
    const [isJoining, setIsJoining] = useState(false);
    const [step, setStep] = useState('enter_code');
    const [isAuthenticating, setIsAuthenticating] = useState(true);

    useEffect(() => {
        if (!isAuthenticated) {
            const inviteCode = searchParams.get('invite');
            const redirectUrl = `/login?redirectTo=/join-company`;
            const finalUrl = inviteCode ? `${redirectUrl}&invite=${inviteCode}` : redirectUrl;
            navigate(finalUrl);
        } else {
            setIsAuthenticating(false);
        }
    }, [isAuthenticated, navigate, searchParams]);

    useEffect(() => {
        const inviteCode = searchParams.get('invite');
        if (inviteCode && !isAuthenticating) {
            formik.setFieldValue('invitationCode', inviteCode);
            handleValidateInvitation(inviteCode);
        }
    }, [searchParams, isAuthenticating]);

    const formik = useFormik({
        initialValues: {
            invitationCode: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            if (step === 'enter_code') {
                await handleValidateInvitation(values.invitationCode);
            } else if (step === 'confirm_join') {
                await handleJoinCompany(values.invitationCode);
            }
        },
    });

    const handleValidateInvitation = async (invitationCode) => {
        setJoinError('');
        setIsJoining(true);

        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            setStep('confirm_join');
        } catch (error) {
            console.error('[invitacion] fallo validando la invitacion:', error);
            setJoinError(t('hardcoded.components_auth_joincompany.error_validando_la_invitacion_verifica'));
        } finally {
            setIsJoining(false);
        }
    };

    const handleJoinCompany = async (invitationCode) => {
        setIsJoining(true);
        setJoinError('');

        try {
            // 🔥 FIXED: Use the correct endpoint for authenticated users
            const response = await api.auth.joinCompany(invitationCode);

            if (response.data) {
                // Update auth state
                localStorage.setItem('access_token', response.data.access_token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                localStorage.setItem('companies', JSON.stringify(response.data.companies));

                setStep('success');
                showSuccess(t('hardcoded.components_auth_joincompany.te_has_unido_exitosamente_a'));

                setTimeout(() => {
                    window.location.href = '/dashboard';
                }, 2000);
            }
        } catch (error) {
            console.error(t('hardcoded.components_auth_joincompany.join_company_error'), error);
            const errorMessage = error.response?.data?.detail || t('hardcoded.components_auth_joincompany.error_al_unirse_a_la');
            setJoinError(errorMessage);

            if (errorMessage.includes(t('hardcoded.components_auth_joincompany.already_a_member'))) {
                showError(t('hardcoded.components_auth_joincompany.ya_eres_miembro_de_esta'));
                setTimeout(() => navigate('/dashboard'), 2000);
            } else {
                setStep('enter_code');
            }
        } finally {
            setIsJoining(false);
        }
    };

    const handleGoBack = () => {
        setStep('enter_code');
        setJoinError('');
        setIsJoining(false);
    };

    const handleGoToDashboard = () => {
        navigate('/dashboard');
    };

    if (isAuthenticating) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    const renderStepContent = () => {
        switch (step) {
            case 'enter_code':
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('inviteUser.success.invitationCode')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('hardcoded.components_auth_joincompany.ingresa_el_codigo_de_invitacion')}</Typography>

                        <TextField
                            fullWidth
                            name="invitationCode"
                            label={t('inviteUser.success.invitationCode')}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            value={formik.values.invitationCode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.invitationCode && Boolean(formik.errors.invitationCode)}
                            helperText={formik.touched.invitationCode && formik.errors.invitationCode}
                            sx={{ mb: 3 }}
                            InputProps={{
                                style: { fontFamily: 'monospace' }
                            }}
                            disabled={isJoining}
                        />

                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                <strong>{t('hardcoded.components_auth_joincompany.como_usuario_existente')}</strong> {t('hardcoded.components_auth_joincompany.seras_agregado_directamente_a_la')}</Typography>
                        </Alert>
                    </Box>
                );

            case 'confirm_join':
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('hardcoded.components_auth_joincompany.confirmar_union_a_empresa')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('hardcoded.components_auth_joincompany.estas_a_punto_de_unirte')}</Typography>

                        <Paper variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <BusinessIcon color="primary" />
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t('inviteUser.success.details')}</Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">{t('hardcoded.components_auth_joincompany.codigo')}</Typography>
                                <Typography variant="body1" fontFamily="monospace" fontWeight={600}>
                                    {formik.values.invitationCode}
                                </Typography>
                            </Box>

                            <Alert severity="warning" variant="outlined">
                                <Typography variant="body2">
                                    <strong>{t('inviteUser.whatHappens.title')}</strong>
                                    <br />{t('hardcoded.components_auth_joincompany.mantendras_acceso_a_todas_tus')}<br />{t('hardcoded.components_auth_joincompany.podras_cambiar_entre_empresas_desde')}<br />{t('hardcoded.components_auth_joincompany.tu_usuario_y_contrasena_seguiran')}<br />{t('hardcoded.components_auth_joincompany.tendras_acceso_inmediato_a_la')}</Typography>
                            </Alert>
                        </Paper>

                        {isJoining && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {t('hardcoded.components_auth_joincompany.procesando_invitacion')}</Typography>
                                <LinearProgress />
                            </Box>
                        )}
                    </Box>
                );

            case 'success':
                return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                        <Typography variant="h4" gutterBottom>
                            {t('hardcoded.components_auth_joincompany.bienvenido')}</Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {t('hardcoded.components_auth_joincompany.te_has_unido_exitosamente_a')}</Typography>

                        <Alert severity="success" sx={{ mt: 3, mb: 3 }}>
                            <Typography variant="body2">
                                {t('hardcoded.components_auth_joincompany.ahora_tienes_acceso_a_multiples')}</Typography>
                        </Alert>

                        <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.components_auth_joincompany.redirigiendo_automaticamente_en_unos_segundos')}</Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}25 100%)`,
                py: 3,
            }}
        >
            <Container maxWidth="sm">
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: theme.shadows[12],
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <ArsenalLogo
                                sx={{
                                    width: 80,
                                    height: 80,
                                    color: theme.palette.primary.main,
                                    mb: 2,
                                }}
                            />
                            <Typography
                                variant="h4"
                                component="h1"
                                fontWeight={700}
                                color="text.primary"
                                gutterBottom
                            >
                                {i18n.t('hardcoded.components_auth_joincompany.unirse_a_empresa')}</Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                            >
                                {step === 'success' ?
                                    t('hardcoded.components_auth_joincompany.proceso_completado') :
                                    t('hardcoded.components_auth_joincompany.hola_name_unete_a_una', { name: user?.name })
                                }
                            </Typography>
                        </Box>

                        {step !== 'success' && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'primary.main', color: 'white' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <PersonAddIcon />
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {user?.name} {user?.last_name}
                                        </Typography>
                                        <Typography variant="caption">
                                            {user?.email}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {joinError && (
                            <Alert
                                severity="error"
                                sx={{ mb: 3 }}
                                onClose={() => setJoinError('')}
                            >
                                {joinError}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={formik.handleSubmit}>
                            {renderStepContent()}

                            {step !== 'success' && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                                    <Button
                                        onClick={step === 'confirm_join' ? handleGoBack : handleGoToDashboard}
                                        color="inherit"
                                        disabled={isJoining}
                                    >
                                        {step === 'confirm_join' ? t('hardcoded.components_auth_joincompany.atras') : t('hardcoded.components_auth_joincompany.ir_al_dashboard')}
                                    </Button>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isJoining || (step === 'enter_code' && !formik.isValid)}
                                        sx={{ minWidth: 140 }}
                                        startIcon={isJoining ? <CircularProgress size={20} color="inherit" /> : null}
                                    >
                                        {isJoining ? t('hardcoded.components_auth_joincompany.procesando') :
                                            step === 'enter_code' ? t('hardcoded.components_auth_joincompany.continuar') : t('hardcoded.components_auth_joincompany.confirmar_union')}
                                    </Button>
                                </Box>
                            )}

                            {step === 'success' && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleGoToDashboard}
                                        sx={{ minWidth: 140 }}
                                    >
                                        {t('hardcoded.components_auth_joincompany.ir_al_dashboard_ahora')}</Button>
                                </Box>
                            )}
                        </Box>

                        {step !== 'success' && (
                            <Box sx={{ textAlign: 'center', mt: 3 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('hardcoded.components_auth_joincompany.necesitas_ayuda_contacta_al_administrador')}</Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default JoinCompany;